import {LoginAPIImpl} from 'app/client/ui/LoginAPI';
import {buildLoginForm} from 'app/client/ui/LoginForm';
import * as css from 'app/client/ui/LoginPagesCss';
import {buildVerifySecondStepForm} from 'app/client/ui/MFAForms';
import {buildVerifyEmailForm} from 'app/client/ui/SignupForms';
import {getPageTitleSuffix} from 'app/common/gristUrls';
import {AuthMethod, ChallengeRequired} from 'app/common/LoginAPI';
import {getGristConfig} from 'app/common/urlUtils';
import {Disposable, dom, Observable} from 'grainjs';

export class LoginPage extends Disposable {
  private _currentStep: Observable<'sign-in' | 'verify-second-step' | 'verify-email'> =
    Observable.create(this, 'sign-in');
  private _email = Observable.create(this, '');
  private _password = Observable.create(this, '');
  private _challengeDetails: ChallengeRequired;
  private _loginAPI = new LoginAPIImpl();

  public buildDom() {
    const suffix = getPageTitleSuffix(getGristConfig());
    document.title = `Sign in${suffix}`;

    return css.pageContainer(this._buildLoginForms());
  }

  private _buildLoginForms() {
    return css.flexJustifyCenter(css.formContainer(
      dom.domComputed(this._currentStep, currentStep => {
        switch (currentStep) {
          case 'sign-in': {
            return dom.create(buildLoginForm, {
              email: this._email,
              password: this._password,
              loginAPI: this._loginAPI,
              onChallengeRequired: (challenge) => {
                this._challengeDetails = challenge;
                this._currentStep.set('verify-second-step');
              },
              onEmailVerificationRequired: () => {
                this._currentStep.set('verify-email');
              },
            });
          }
          case 'verify-second-step': {
            return [
              dom.create(buildVerifySecondStepForm, {
                theme: 'login',
                onSuccess: (redirectUrl) => { window.location.assign(redirectUrl as string); },
                onSubmit: (verificationCode, session, authMethod) => this._submitVerificationCode({
                  verificationCode,
                  session,
                  authMethod,
                }),
                onResendCode: () => this._resubmitLogin('SMS'),
                onUseAuthApp: () => this._resubmitLogin('TOTP'),
                onUseSMS: () => this._resubmitLogin('SMS'),
                challengeDetails: this._challengeDetails,
                showBackButton: true,
                backButtonText: '← Back to sign in',
                onBack: () => {
                  this._email.set('');
                  this._password.set('');
                  this._currentStep.set('sign-in');
                },
              }),
            ];
          }
          case 'verify-email': {
            return dom.create(buildVerifyEmailForm, {
              loginAPI: this._loginAPI,
              email: this._email,
              headingText: 'Verification Required',
              footer: () => css.text(
                css.textButton('← Back to sign in',
                  dom.on('click', () => {
                    this._email.set('');
                    this._password.set('');
                    this._currentStep.set('sign-in');
                  }),
                ),
              ),
            });
          }
        }
      })
    ));
  }

  private async _submitVerificationCode({authMethod, verificationCode, session}: {
    authMethod: AuthMethod;
    verificationCode: string;
    session: string;
  }) {
    return this._loginAPI.logInSecondStep({
      authMethod,
      verificationCode,
      session,
      username: this._challengeDetails.username,
    });
  }

  private async _resubmitLogin(preferredMfaMethod: AuthMethod) {
    const result = await this._loginAPI.logIn(this._email.get(), this._password.get(), {
      preferredMfaMethod,
    });
    if (!result.isChallengeRequired) { throw new Error('Unknown error occurred.'); }

    return result;
  }
}
